import React, { useEffect, useState, useRef } from 'react';
import Seo from '../components/Seo';

import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient, { gamesAPI, monetizationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AdBanner from '../components/AdBanner';
import VirtualGamepad from '../components/VirtualGamepad';

const CAPTURE_SNIPPET = `<script>(function(){window.addEventListener('message',function(e){if(!e.data||e.data.type!=='GAME_CAPTURE')return;try{var c=document.querySelector('canvas');if(!c)return;requestAnimationFrame(function(){try{var t=document.createElement('canvas');var w=800;var h=Math.max(1,Math.round(w*c.height/Math.max(c.width,1)));t.width=w;t.height=h;var ctx=t.getContext('2d');ctx.drawImage(c,0,0,w,h);var d=ctx.getImageData(0,0,w,h).data;var sum=0;for(var i=0;i<d.length;i+=4000){sum+=d[i]+d[i+1]+d[i+2];}if(sum<30)return;parent.postMessage({type:'GAME_SNAPSHOT',data:t.toDataURL('image/jpeg',0.75)},'*');}catch(err){}});}catch(err){}});})();</script>`;

const GamePlayer = () => {
  // Support both new and legacy share URL formats
  // New: /play/:slug/:gameId
  // Legacy: /play/:slugAndId (e.g., make-a-recreation-of-one-08f04bb2-064a-458f-9453-b927e9891cb3)
  const params = useParams();
  let slug = params.slug;
  let gameId = params.gameId;

  if (!gameId && params.slugAndId) {
    // Extract UUID from the combined slug-and-id
    const match = params.slugAndId.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);
    if (match) {
      gameId = match[0];
      slug = params.slugAndId
        .slice(0, -match[0].length)
        .replace(/[-]+$/g, '') || 'game';
    }
  }

  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adsEnabled, setAdsEnabled] = useState(false);
  const iframeRef = useRef(null);

  const [impressionTracked, setImpressionTracked] = useState(false);
  const [remixing, setRemixing] = useState(false);
  const captureSentRef = useRef(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleRemix = async () => {
    if (!gameId) return;
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    setRemixing(true);
    try {
      const { data: session } = await apiClient.post(`/ai/sessions/remix/${gameId}`);
      navigate(`/create?sessionId=${session.session_id}`);
    } catch (err) {
      console.error('Failed to remix game', err);
      setRemixing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!gameId) {
        setError('Invalid game link');
        setLoading(false);
        return;
      }

      try {
        const data = await gamesAPI.getPublicGame(gameId);
        setGame(data);
        setAdsEnabled(!!(data.ads_enabled || data.monetization?.enabled));
      } catch (err) {
        console.error('Error loading public game', err);
        setError('Game not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, gameId]);

  // Track ad impression once per page load if ads are enabled
  useEffect(() => {
    const trackImpression = async () => {
      if (!gameId || !adsEnabled || impressionTracked) return;
      try {
        await monetizationAPI.trackImpression(gameId, 'banner');
        setImpressionTracked(true);
      } catch (err) {
        console.error('Failed to track ad impression', err);
      }
    };

    trackImpression();
  }, [gameId, adsEnabled, impressionTracked]);

  // Receive gameplay snapshots from the sandboxed iframe and store as thumbnail
  useEffect(() => {
    const onMessage = async (e) => {
      if (!e.data || e.data.type !== 'GAME_SNAPSHOT' || captureSentRef.current) return;
      if (!gameId || !e.data.data) return;
      captureSentRef.current = true;
      try {
        await apiClient.post(`/arcade/games/${gameId}/thumbnail`, { image_base64: e.data.data });
      } catch (err) {
        console.error('Thumbnail upload failed', err);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [gameId]);

  const fullUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-gray-300">
        Loading game...
      </div>
    );
  }

  if (error || !game) {
    const fallbackTitle = 'Game not found - GameOS.dev';
    const fallbackDesc = 'This shared game link is invalid or the game is no longer available.';

    return (
      <>
        <Seo title={fallbackTitle} description={fallbackDesc} url={fullUrl || undefined} />
        <div className="min-h-screen bg-black flex flex-col items-center justify-center text-gray-300">
          <p className="mb-4">{error || 'Game not found'}</p>
          <button
            type="button"
            className="px-4 py-2 rounded bg-purple-600 text-white"
            onClick={() => navigate('/')}
          >
            Back to GameOS [BETA]
          </button>
        </div>
      </>
    );
  }

  // Function to send start event to iframe
  const sendStartEventToIframe = () => {
    if (!iframeRef.current) return;
    try {
      // Try postMessage first for games that listen for it
      iframeRef.current.contentWindow.postMessage({ type: 'GAME_START' }, '*');
    } catch (e) {
      // Ignore cross-origin errors; many AI games are same-document via srcDoc
    }

    // Fallback: try simulating spacebar keydown for games that only listen for keyboard
    try {
      const event = new KeyboardEvent('keydown', {
        key: ' ',
        code: 'Space',
        keyCode: 32,
        which: 32,
        bubbles: true,
      });
      iframeRef.current.contentWindow.dispatchEvent(event);
    } catch (e) {
      // Ignore if we can't dispatch events (cross-origin)
    }

    // Capture a real gameplay frame ~3s after start if this game has no thumbnail yet
    if (game && !game.thumbnail_path && !captureSentRef.current) {
      setTimeout(() => {
        try {
          iframeRef.current?.contentWindow.postMessage({ type: 'GAME_CAPTURE' }, '*');
        } catch (e) {
          // Ignore
        }
      }, 3000);
    }
  };

  const srcDoc = (() => {
    const code = game.code || '';
    const lower = code.toLowerCase();

    // If this is already a full HTML document, use it directly (with capture hook).
    if (lower.includes('<html')) {
      return code.includes('</body>')
        ? code.replace('</body>', CAPTURE_SNIPPET + '</body>')
        : code + CAPTURE_SNIPPET;
    }

    // Otherwise, treat it as JavaScript and wrap in an HTML shell.
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${game.title || 'Game'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; overflow: hidden; background: #0b1220; }
  </style>
</head>
<body>
  <script>
    try {
      ${game.code || ''}
    } catch (e) {
      document.body.innerHTML = '<div style="color: white; padding: 20px; font-family: Arial;">Error loading game: ' + e.message + '</div>';
      console.error('Game error:', e);
    }
  </script>
  ${CAPTURE_SNIPPET}
</body>
</html>`;
  })();

  const pageTitle = game.title ? `${game.title} - Play on GameOS.dev` : 'Play Game - GameOS.dev';
  const pageDescription = game.description || game.initial_prompt || 'Play this AI-generated game on GameOS.dev.';
  const cardImage = `${process.env.REACT_APP_BACKEND_URL}/api/meta/games/${gameId}/card.png`;
  const videoGameSchema = {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: game.title || 'AI-generated game',
    description: pageDescription,
    url: fullUrl || `https://gameos.dev/play/game/${gameId}`,
    image: cardImage,
    gamePlatform: 'Web Browser',
    applicationCategory: 'Game',
    operatingSystem: 'Any',
    playMode: 'SinglePlayer',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
    publisher: { '@type': 'Organization', name: 'GameOS', url: 'https://gameos.dev' },
    ...(game.created_at ? { datePublished: String(game.created_at).slice(0, 10) } : {}),
  };

  return (
    <>
      <Seo
        title={pageTitle}
        description={pageDescription}
        url={fullUrl || undefined}
        image={cardImage}
        jsonLd={videoGameSchema}
      />
      <div className="min-h-screen bg-black flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/80">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">GO</span>
          </div>
          <div>
            <div className="text-sm font-semibold text-white">{game.title}</div>
            <div className="text-xs text-gray-400">
              {game.creator_username && (
                <>
                  by{' '}
                  <Link
                    to={`/creator/${encodeURIComponent(game.creator_username)}`}
                    data-testid="player-creator-link"
                    className="text-purple-300 hover:text-purple-200"
                  >
                    {game.creator_username}
                  </Link>
                  {' · '}
                </>
              )}
              Built with GameOS [BETA]
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-xs text-gray-400 hover:text-white"
        >
          gameos.dev
        </button>
      </div>

      {/* AdSense banner when ads are enabled */}
      {adsEnabled && <AdBanner />}

      <div className="flex-1 bg-black flex flex-col items-center justify-center p-2 gap-3">
        {/* Mobile-friendly start controls */}
        <div className="flex gap-2 flex-wrap justify-center">
          <button
            type="button"
            onClick={sendStartEventToIframe}
            className="px-4 py-2 rounded bg-purple-600 text-white text-sm font-semibold shadow hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            Tap to Start (Space)
          </button>
          <button
            type="button"
            onClick={sendStartEventToIframe}
            className="px-4 py-2 rounded bg-gray-800 text-gray-100 text-sm border border-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Resume / Continue
          </button>
          <button
            type="button"
            data-testid="remix-game-btn"
            onClick={handleRemix}
            disabled={remixing}
            className="px-4 py-2 rounded bg-gradient-to-r from-pink-600 to-purple-600 text-white text-sm font-semibold shadow hover:from-pink-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-pink-400 disabled:opacity-60"
          >
            {remixing ? 'Remixing...' : 'Remix with AI'}
          </button>
        </div>
        <div className="w-full max-w-5xl aspect-video border border-white/10 rounded-lg overflow-hidden bg-black">
          <iframe
            ref={iframeRef}
            title={game.title || 'Game'}
            className="w-full h-full border-0"
            sandbox="allow-scripts"
            srcDoc={srcDoc}
          />
        </div>
        {/* On-screen controls for touch devices */}
        <VirtualGamepad iframeRef={iframeRef} />
      </div>
    </div>
    </>
  );
};

export default GamePlayer;
